import './Tabs.scss'
import { useId, useRef, type ComponentProps, type KeyboardEvent, type ReactNode } from 'react'
import { clx } from '../clx'

export interface TabItem {
  value: string
  label: ReactNode
  /** Not rendered at all (e.g. an RBAC-gated tab). */
  isHidden?: boolean
  /** Rendered but not selectable; skipped by the arrow keys. */
  isDisabled?: boolean
}

export interface TabsProps extends Omit<ComponentProps<'div'>, 'onChange' | 'children'> {
  tabs: TabItem[]
  /** The selected tab's value. Fully controlled: the caller owns it (and syncs the URL). */
  value: string
  onChange: (value: string) => void
  /** Accessible name of the tablist. Give this or `labelledBy`. */
  label?: string
  /** Id of an element that names the tablist. */
  labelledBy?: string
  /**
   * Panel content for a tab value. Without it Tabs renders only the tablist
   * and the caller renders the content (no aria-controls is set then).
   */
  renderPanel?: (value: string) => ReactNode
  /**
   * Render every visible tab's panel, hiding the inactive ones, so their
   * state (e.g. form fields) survives a tab switch. Default: only the
   * selected panel is mounted.
   */
  keepPanelsMounted?: boolean
}

/**
 * WAI-ARIA tabs with automatic activation: ←/→ (wrapping) and Home/End move
 * focus and select in one step; hidden and disabled tabs are skipped. One
 * tab is in the tab order (roving tabindex).
 */
export function Tabs({
  tabs,
  value,
  onChange,
  label,
  labelledBy,
  renderPanel,
  keepPanelsMounted = false,
  id,
  className,
  ref,
  ...rest
}: TabsProps) {
  const generated = useId()
  const base = id ?? `tabs-${generated}`
  const buttons = useRef(new Map<string, HTMLButtonElement>())

  const indexed = tabs.map((tab, index) => ({ ...tab, index })).filter((tab) => !tab.isHidden)
  const enabled = indexed.filter((tab) => !tab.isDisabled)
  const selected = enabled.find((tab) => tab.value === value)
  const focusable = selected ?? enabled[0]

  const tabId = (index: number) => `${base}-tab-${index}`
  const panelId = (index: number) => `${base}-panel-${index}`
  const hasPanel = (tabValue: string) =>
    renderPanel !== undefined && (keepPanelsMounted || tabValue === selected?.value)

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!enabled.length) return
    const current = enabled.findIndex((tab) => buttons.current.get(tab.value) === event.target)
    let next: number
    switch (event.key) {
      case 'ArrowRight':
        next = current < 0 ? 0 : (current + 1) % enabled.length
        break
      case 'ArrowLeft':
        next = current < 0 ? enabled.length - 1 : (current - 1 + enabled.length) % enabled.length
        break
      case 'Home':
        next = 0
        break
      case 'End':
        next = enabled.length - 1
        break
      default:
        return
    }
    event.preventDefault()
    const target = enabled[next]
    if (!target) return
    buttons.current.get(target.value)?.focus()
    if (target.value !== value) onChange(target.value)
  }

  return (
    <div ref={ref} {...rest} id={base} className={clx('tabs', className)}>
      <div
        role="tablist"
        aria-orientation="horizontal"
        {...(label !== undefined && { 'aria-label': label })}
        {...(labelledBy !== undefined && { 'aria-labelledby': labelledBy })}
        className="tabs__list"
        onKeyDown={onKeyDown}
      >
        {indexed.map((tab) => {
          const isSelected = tab === selected
          return (
            <button
              key={tab.value}
              ref={(node) => {
                if (node) buttons.current.set(tab.value, node)
                else buttons.current.delete(tab.value)
              }}
              type="button"
              role="tab"
              id={tabId(tab.index)}
              className="tabs__tab"
              aria-selected={isSelected}
              {...(hasPanel(tab.value) && { 'aria-controls': panelId(tab.index) })}
              tabIndex={tab === focusable ? 0 : -1}
              disabled={tab.isDisabled}
              onClick={() => {
                if (!isSelected) onChange(tab.value)
              }}
            >
              {tab.label}
            </button>
          )
        })}
      </div>
      {renderPanel &&
        indexed
          .filter((tab) => hasPanel(tab.value))
          .map((tab) => (
            <div
              key={tab.value}
              role="tabpanel"
              id={panelId(tab.index)}
              aria-labelledby={tabId(tab.index)}
              tabIndex={0}
              hidden={tab !== selected}
              className="tabs__panel"
            >
              {renderPanel(tab.value)}
            </div>
          ))}
    </div>
  )
}
